// Iter v34z (#5) — Master Vendor (Procurement) — CRUD page + autofill source for Vendor PO.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button, Field, Input, Modal, Textarea } from "@/components/ui-kit";

const blank = {
  name: "", address: "", contact: "", phone: "", email: "",
  npwp: "", bank: "", note: "", is_active: true,
};

export default function MasterVendor() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("vendors.manage");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank);
  const [q, setQ] = useState("");

  const load = () => api.get("/vendors").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(blank); setOpen(true); };
  const openEdit = (v) => { setEditId(v.id); setForm({ ...blank, ...v }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nama vendor wajib diisi"); return; }
    try {
      if (editId) await api.patch(`/vendors/${editId}`, form);
      else await api.post("/vendors", form);
      toast.success(editId ? "Vendor diperbarui" : "Vendor ditambahkan");
      setOpen(false); setEditId(null); setForm(blank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Hapus vendor "${name}"?`)) return;
    await api.delete(`/vendors/${id}`);
    toast.success("Dihapus"); load();
  };

  const filtered = rows.filter((v) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [v.name, v.contact, v.phone, v.email, v.address, v.npwp].some((x) => (x || "").toLowerCase().includes(s));
  });

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Procurement · Master Data"
        title="Master Vendor"
        description="Daftar vendor/supplier yang dipakai saat membuat PO Vendor (autofill nama, alamat, kontak, telp)."
        icon={Users}
        action={canEdit && (
          <Button onClick={openNew} data-testid="add-vendor-btn">
            <Plus className="h-4 w-4" /> Vendor Baru
          </Button>
        )}
      />

      <Card>
        <div className="p-4 border-b border-stone-200">
          <Input placeholder="Cari vendor (nama, contact, NPWP, alamat)..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="vendor-search" className="max-w-md" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
                <th className="py-3 px-3">Nama Vendor</th>
                <th className="py-3 px-3">Contact</th>
                <th className="py-3 px-3">Telp</th>
                <th className="py-3 px-3">Email</th>
                <th className="py-3 px-3">NPWP</th>
                <th className="py-3 px-3 text-center">Status</th>
                <th className="py-3 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-stone-400 text-xs">
                  {q ? `Tidak ada vendor yang cocok dengan "${q}"` : "Belum ada vendor. Klik 'Vendor Baru'."}
                </td></tr>
              )}
              {filtered.map((v, idx) => (
                <tr key={v.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`vendor-row-${idx}`}>
                  <td className="py-2.5 px-3 font-medium">{v.name}</td>
                  <td className="py-2.5 px-3 text-stone-600">{v.contact || "—"}</td>
                  <td className="py-2.5 px-3 text-stone-600 font-mono text-xs">{v.phone || "—"}</td>
                  <td className="py-2.5 px-3 text-stone-600 text-xs">{v.email || "—"}</td>
                  <td className="py-2.5 px-3 text-stone-600 font-mono text-xs">{v.npwp || "—"}</td>
                  <td className="py-2.5 px-3 text-center">
                    {v.is_active ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold uppercase">Aktif</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 bg-stone-200 text-stone-600 rounded font-semibold uppercase">Nonaktif</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {canEdit && <button onClick={() => openEdit(v)} className="text-stone-500 hover:text-terra-700 px-2" data-testid={`vendor-edit-${idx}`}><Pencil className="h-3.5 w-3.5" /></button>}
                    {canEdit && <button onClick={() => del(v.id, v.name)} className="text-stone-500 hover:text-red-700 px-2" data-testid={`vendor-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Vendor" : "Vendor Baru"} size="lg">
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nama Vendor" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="PT. SINAR SUKSES DISTRIBUSI" data-testid="vendor-name-input" />
            </Field>
            <Field label="Up / Contact Person">
              <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Tim Sales / Marketing" />
            </Field>
            <Field label="Alamat">
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Kawasan Industri Cikarang Blok C-21" />
            </Field>
            <Field label="Telepon">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(021) 8990-1234" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sales@vendor.co.id" />
            </Field>
            <Field label="NPWP">
              <Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} placeholder="01.234.567.8-901.000" />
            </Field>
            <Field label="Rekening Bank">
              <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} placeholder="BCA 1234567890 a/n PT Vendor" />
            </Field>
            <Field label="Status">
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                Vendor aktif (muncul di dropdown PO)
              </label>
            </Field>
          </div>
          <Field label="Catatan">
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="secondary" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} data-testid="vendor-submit"><Save className="h-4 w-4" /> {editId ? "Update" : "Simpan"}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
